import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoginPromptDialog from '../components/LoginPromptDialog';
import UpgradePromptDialog from '../components/UpgradePromptDialog';
import { recordGuestGatedOpen } from '../components/GuestUpgradeNudge';
import { logPaywall } from '../lib/paywallAnalytics';

const PREMIUM_TIERS = new Set(['premium', 'lifetime', 'collaborator']);
// Staff roles get full access regardless of subscription tier, in any
// environment (so the owner/admins are never gated in production).
const STAFF_ROLES = new Set(['super_admin', 'moderator', 'admin']);
const hasFullAccess = (user) =>
  !!user && (STAFF_ROLES.has(user.role) || PREMIUM_TIERS.has(user.subscription_tier || 'free'));

/**
 * usePremiumGate — gate Premium-only features with a two-step funnel:
 *   • Guest        → friendly "sign in to unlock" prompt (free account).
 *   • Free user    → "Upgrade to Lumera+" paywall (→ /pricing).
 *   • Premium/trial→ allowed.
 *
 * Usage:
 *   const { requirePremium, gatePremium, dialog, isPremium } = usePremiumGate();
 *   const onOpen = () => { if (!requirePremium("Commentaries")) return; open(); };
 *   {dialog}  // render once
 */
export const usePremiumGate = () => {
  const { user } = useAuth();
  const [loginFeature, setLoginFeature] = useState(null);
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  const isPremium = hasFullAccess(user);

  const requirePremium = useCallback((featureLabel) => {
    if (!user) {
      setLoginFeature(featureLabel || null);
      recordGuestGatedOpen();
      logPaywall('shown', featureLabel, 'guest');
      return false;
    }
    if (!hasFullAccess(user)) {
      setUpgradeFeature(featureLabel || null);
      logPaywall('shown', featureLabel, 'free');
      return false;
    }
    return true;
  }, [user]);

  const gatePremium = useCallback((fn, featureLabel) => {
    if (requirePremium(featureLabel)) return fn();
    return undefined;
  }, [requirePremium]);

  const dialog = (
    <>
      <LoginPromptDialog open={loginFeature !== null} feature={loginFeature} onClose={() => setLoginFeature(null)} />
      <UpgradePromptDialog open={upgradeFeature !== null} feature={upgradeFeature} onClose={() => setUpgradeFeature(null)} />
    </>
  );

  return { user, isPremium, requirePremium, gatePremium, dialog };
};

export default usePremiumGate;
